import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Trash2,
  Upload,
  UploadCloud,
} from "lucide-react";
import { Button } from "components/ui/button";
import { useI18n } from "i18n";
import api from "lib/api";

/**
 * Uploader de fotos do evento — drag & drop de vários arquivos.
 *
 * Regras do fluxo (ver backend `views/photo.py` e `services/photo_processing.py`):
 *
 * - os arquivos entram numa fila e sobem **um a um**, nunca em paralelo: cada
 *   requisição carrega uma foto inteira e o servidor grava o original como veio;
 * - terminado o envio, a foto NÃO está pronta — ela fica pendente até o crontab
 *   convertê-la para JPEG 1080p e extrair a biometria dos rostos. Por isso o
 *   item continua em "processando" e o componente faz polling do status;
 * - reenviar o MESMO arquivo (hash idêntico) não duplica nem falha: o backend
 *   responde 200 e a foto existente volta para a fila do worker — o item é
 *   marcado como "atualizada" e segue o mesmo polling de uma foto nova.
 */

const POLL_INTERVAL_MS = 4000;

// Estados do item na fila (os três primeiros são locais; o resto vem da API).
const QUEUED = "queued";
const UPLOADING = "uploading";
const PROCESSING = "processing";
const DONE = "done";
const FAILED = "failed";

const STATUS_STYLE = {
  [QUEUED]: "text-muted-foreground",
  [UPLOADING]: "text-blue-500",
  [PROCESSING]: "text-amber-500",
  [DONE]: "text-emerald-500",
  [FAILED]: "text-red-500",
};

let itemSeq = 0;

export default function PhotoUploader({ eventId, onUploaded }) {
  const { t, tf } = useI18n();
  const [items, setItems] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileInput = useRef(null);
  const sending = useRef(false);
  const onUploadedRef = useRef(onUploaded);

  useEffect(() => {
    onUploadedRef.current = onUploaded;
  }, [onUploaded]);

  const updateItem = useCallback((id, patch) => {
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item))
    );
  }, []);

  const addFiles = useCallback((fileList) => {
    const files = Array.from(fileList || []).filter((file) =>
      file.type ? file.type.startsWith("image/") : true
    );
    if (!files.length) return;

    setItems((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: `item-${(itemSeq += 1)}`,
        file,
        name: file.name,
        size: file.size,
        status: QUEUED,
        progress: 0,
        photoId: null,
        error: "",
      })),
    ]);
  }, []);

  // ---------------------------------------------------------------- envio
  // Um arquivo por vez: pega o próximo `queued` sempre que o envio anterior
  // termina. `sending` evita que dois efeitos disparem o mesmo item.
  useEffect(() => {
    if (sending.current) return;
    const next = items.find((item) => item.status === QUEUED);
    if (!next) return;

    sending.current = true;
    (async () => {
      updateItem(next.id, { status: UPLOADING, progress: 0 });
      const form = new FormData();
      form.append("file", next.file);

      try {
        const res = await api.post(`/api/events/${eventId}/photos/`, form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (event) => {
            if (!event.total) return;
            updateItem(next.id, {
              progress: Math.round((event.loaded * 100) / event.total),
            });
          },
        });
        updateItem(next.id, {
          status: PROCESSING,
          progress: 100,
          photoId: res.data?.id || null,
          // 200 = a foto já estava no evento e foi atualizada; 201 = nova.
          replaced: res.status === 200,
          file: null, // liberamos o File: a fila pode ter centenas de fotos
        });
      } catch (err) {
        updateItem(next.id, {
          status: FAILED,
          error: err.response?.data?.detail || t("photo.uploadError"),
          file: null,
        });
      } finally {
        sending.current = false;
      }
    })();
  }, [items, eventId, updateItem, t]);

  // -------------------------------------------------------------- polling
  // Enquanto houver foto em processamento, pergunta o estado ao backend. O
  // tratamento é feito pelo crontab, então isso pode levar alguns minutos.
  useEffect(() => {
    const pending = items.filter((item) => item.status === PROCESSING && item.photoId);
    if (!pending.length) return undefined;

    const timer = setInterval(async () => {
      try {
        const ids = pending.map((item) => item.photoId).join(",");
        const res = await api.get(`/api/events/${eventId}/photos/status/`, {
          params: { ids },
        });
        const byId = new Map((res.data || []).map((photo) => [photo.id, photo]));

        // Os ids que PERGUNTAMOS: um que nao volte deixou de existir.
        const asked = new Set(pending.map((item) => item.photoId));
        let finished = false;
        setItems((prev) =>
          prev.map((item) => {
            if (item.status !== PROCESSING || !item.photoId) return item;
            const photo = byId.get(item.photoId);
            if (!photo) {
              if (!asked.has(item.photoId)) return item;
              // O worker viu que o evento ja tinha esta mesma imagem e a
              // absorveu (ficou a copia mais completa). Nao e erro.
              finished = true;
              return { ...item, status: DONE, merged: true };
            }
            if (photo.status === "done") {
              finished = true;
              return {
                ...item,
                status: DONE,
                faceCount: photo.face_count,
                faceStatus: photo.face_status,
                thumbnail: photo.thumbnail_url,
              };
            }
            if (photo.status === "failed") {
              finished = true;
              return { ...item, status: FAILED, error: photo.error || "" };
            }
            return item;
          })
        );
        if (finished && onUploadedRef.current) onUploadedRef.current();
      } catch {
        // Uma falha de polling não invalida o upload — tentamos no próximo tick.
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [items, eventId]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const counts = items.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {});
  const busy = (counts[QUEUED] || 0) + (counts[UPLOADING] || 0) + (counts[PROCESSING] || 0);

  // Quantas fotos já subiram — o envio termina muito antes do tratamento, e sem
  // este número a fila parece parada em "0 prontas" enquanto o worker não roda.
  // Conta tudo que já chegou ao servidor: em processamento, pronta, e a que
  // falhou DEPOIS do envio (tem `photoId`).
  const uploaded = items.filter(
    (item) =>
      item.status !== QUEUED &&
      item.status !== UPLOADING &&
      !(item.status === FAILED && !item.photoId)
  ).length;

  return (
    <div className="w-full space-y-4">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => fileInput.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileInput.current?.click();
        }}
        className={`flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${
          dragging
            ? "border-primary bg-primary/5"
            : "border-border hover:border-primary/50 hover:bg-muted/30"
        }`}
      >
        <UploadCloud className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium">{t("photo.dropTitle")}</p>
        <p className="max-w-md text-xs text-muted-foreground">{t("photo.dropHint")}</p>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {items.length > 0 && (
        <div className="rounded-xl border border-border">
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <div className="text-sm">
              <span className="font-medium">
                {tf("photo.queueSummary", {
                  done: counts[DONE] || 0,
                  total: items.length,
                })}
              </span>
              <span className="ml-2 text-xs text-muted-foreground">
                {tf("photo.queueUploaded", {
                  count: uploaded,
                  total: items.length,
                })}
                {busy > 0 && <> · {tf("photo.queueBusy", { count: busy })}</>}
              </span>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setItems((prev) =>
                  prev.filter(
                    (item) => ![DONE, FAILED].includes(item.status)
                  )
                )
              }
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {t("photo.clearFinished")}
            </Button>
          </div>

          <ul className="max-h-96 divide-y divide-border overflow-y-auto">
            {items.map((item) => (
              <li key={item.id} className="flex items-center gap-3 px-4 py-2.5">
                <StatusIcon status={item.status} />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{item.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatSize(item.size)}
                    {item.replaced && <> · {t("photo.replaced")}</>}
                    {item.merged && <> · {t("photo.merged")}</>}
                    {item.status === DONE && item.faceCount != null && (
                      <>
                        {" · "}
                        {tf("photo.facesFound", { count: item.faceCount })}
                      </>
                    )}
                    {item.error && <> · {item.error}</>}
                  </p>

                  {item.status === UPLOADING && (
                    <div className="mt-1 h-1 w-full overflow-hidden rounded bg-muted">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${item.progress}%` }}
                      />
                    </div>
                  )}
                </div>

                <span
                  className={`shrink-0 text-xs font-medium ${STATUS_STYLE[item.status]}`}
                >
                  {t(`photo.status.${item.status}`)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === DONE) return <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />;
  if (status === FAILED) return <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />;
  if (status === QUEUED) return <Upload className="h-4 w-4 shrink-0 text-muted-foreground" />;
  return <Loader2 className="h-4 w-4 shrink-0 animate-spin text-blue-500" />;
}

function formatSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}
